// Eskom PARCC — Removal history (WS3, 1 Jul 2026 incident). Admin-only card that
// replaces the single "Restore last removal" button: every "Remove current
// population" purge is a RemovalBatch, and each batch — old or new — is
// individually restorable. A restore un-deletes ONLY that batch's rows, as-was
// (review state untouched; contrast the import-revive, which starts a fresh
// cycle). Confirmation is the codebase's inline typed-confirm pattern (type
// RESTORE), mirroring the purge card's type-DELETE.
import { useCallback, useEffect, useState } from 'react';
import { Card, Button, EmptyState, LoadingState, ErrorState, useToast } from '../../components/ui';
import { adminApi, type RemovalBatchRow } from '../../lib/adminApi';

/** SAST timestamp for a batch row (the audit trail is UTC; the team works in Johannesburg). */
function formatSast(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

interface RemovalHistoryProps {
  /** Bump to reload the list (the purge card increments it after a removal). */
  reloadKey?: number;
  /** Called after a successful restore so the page can refetch the register. */
  onRestored?: () => void;
}

export function RemovalHistory({ reloadKey = 0, onRestored }: RemovalHistoryProps): React.JSX.Element {
  const { show } = useToast();
  const [batches, setBatches] = useState<RemovalBatchRow[] | null>(null);
  const [error, setError] = useState<string>('');
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await adminApi.listRemovalBatches();
      setBatches(r.batches);
    } catch (err) {
      setBatches([]);
      setError(err instanceof Error ? err.message : 'Failed to load the removal history.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  async function handleRestore(batch: RemovalBatchRow): Promise<void> {
    if (confirmText.trim() !== 'RESTORE') {
      show('Type RESTORE to confirm restoring this removal.', 'error');
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.restorePopulation(batch.id);
      if (r.noop) {
        show(
          r.message ??
            'Nothing left to restore in this batch — its rows have since been re-imported or removed again.',
          'error',
        );
      } else {
        show(
          `Restored ${r.transactions.toLocaleString('en-ZA')} transaction(s) and ${r.findings.toLocaleString('en-ZA')} finding(s) from the removal of ${formatSast(batch.at)}.`,
          'success',
        );
      }
      setConfirmingId(null);
      setConfirmText('');
      onRestored?.();
      await load();
    } catch (err) {
      show(err instanceof Error ? err.message : 'Failed to restore the removal.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="section-head">
        <div>
          <h3>Removal history</h3>
          <div className="small">
            Every population removal is kept as a restorable batch. Restoring a batch brings back
            exactly the rows it removed — including reviews — and nothing else.
          </div>
        </div>
      </div>

      {batches === null ? (
        <LoadingState message="Loading the removal history…" />
      ) : error ? (
        <ErrorState message="Could not load the removal history." detail={error} />
      ) : batches.length === 0 ? (
        <EmptyState message="Nothing to restore yet — no population removals are recorded." />
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {batches.map((batch) => (
            <li
              key={batch.id}
              data-testid={`removal-batch-${batch.id}`}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                border: '1px solid var(--border, #d5d9e0)',
                borderRadius: 8,
              }}
            >
              <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <div>
                  <strong>{formatSast(batch.at)}</strong>
                  {batch.actorEmail ? <span className="small"> — {batch.actorEmail}</span> : null}
                </div>
                <div className="small">
                  {(batch.txCount ?? 0).toLocaleString('en-ZA')} transaction(s),{' '}
                  {(batch.findingCount ?? 0).toLocaleString('en-ZA')} finding(s)
                  {batch.note ? ` · ${batch.note}` : ''}
                </div>
              </div>
              {batch.restoredAt ? (
                <span className="small">
                  Restored on {formatSast(batch.restoredAt)}
                  {batch.restoredBy ? ` by ${batch.restoredBy}` : ''}
                </span>
              ) : confirmingId === batch.id ? (
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="RESTORE"
                    disabled={busy}
                    aria-label="Type RESTORE to confirm restoring this removal"
                    data-testid={`restore-confirm-${batch.id}`}
                  />
                  <Button
                    variant="primary"
                    onClick={() => void handleRestore(batch)}
                    disabled={busy || confirmText.trim() !== 'RESTORE'}
                    data-testid={`restore-apply-${batch.id}`}
                  >
                    {busy ? 'Restoring…' : 'Confirm restore'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setConfirmingId(null);
                      setConfirmText('');
                    }}
                    disabled={busy}
                  >
                    Cancel
                  </Button>
                </span>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setConfirmingId(batch.id);
                    setConfirmText('');
                  }}
                  disabled={busy}
                  data-testid={`restore-open-${batch.id}`}
                >
                  Restore…
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
